import axios from 'axios';

const HTTP_NOCONTENT = 204;

/**
 * Returns an empty object when "no content".
 *
 * @param {Object} response
 * @returns {Object}
 */
const handleResponseSuccess = response => {
    // Safery check, sometimes response is undefined
    if (!response) {
        return {};
    }

    const { status, data } = response;

    switch (status) {
        case HTTP_NOCONTENT:
            return {};
        default:
            return data || response;
    }
};

/**
 * Add logging when error with Axios, will log on server or browser
 * https://github.com/axios/axios#handling-errors
 *
 * @param {Error} error
 * @param {String} url
 * @param {Object} options
 * @param {Object} data
 * @returns {Promise}
 */
const handleResponseError = (error, url, options, data) => {
    let message;
    let responseData;

    if (error.response) {
        message = 'The request was made and the server responded with a status code that falls out of the range of 2xx';
        responseData = error.response.data;
    } else if (error.request) {
        message = 'The request was made but no response was received';
    } else {
        message = 'Something happened in setting up the request that triggered an Error';
    }

    const logData = {
        customMessage: message
    };

    if (error.message) {
        logData.errorMessage = error.message;
    }

    if (responseData) {
        logData.responseData = responseData;
    }

    let completeURL = url;

    // Concat queryParams to have the complete URL
    if (options && options.params) {
        completeURL += `?${Object.keys(options.params)
            .map(key => `${key}=${options.params[key]}`)
            .join('&')}`;
    }

    logData.url = completeURL;

    if (data) {
        logData.payload = data;
    }

    return Promise.reject(new Error(JSON.stringify(logData)));
};

/**
 * @param {String} url
 * @param {Object} options - Additional config (headers, queryParams...)
 * @returns {Promise}
 */
const get = async (url, options = {}) => {
    try {
        const response = await axios.get(url, options);

        return handleResponseSuccess(response);
    } catch (error) {
        return handleResponseError(error, url, options);
    }
};

/**
 * @param {String} url
 * @param {Object} data - Body of the request
 * @param {Object} options - Additional config (headers, queryParams...)
 * @returns {Promise}
 */
const post = async (url, data = {}, options = {}) => {
    try {
        const response = await axios.post(url, data, options);

        return handleResponseSuccess(response);
    } catch (error) {
        return handleResponseError(error, url, options, data);
    }
};

/**
 * @param {String} url
 * @param {Object} data - Body of the request
 * @param {Object} config - Additional config (headers, queryParams...)
 * @returns {Promise}
 */
const put = async (url, data = {}, options = {}) => {
    try {
        const response = await axios.put(url, data, options);

        return handleResponseSuccess(response);
    } catch (error) {
        return handleResponseError(error, url, options, data);
    }
};

export {
    get,
    post,
    put
};
